import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import Navbar from '../components/Navbar';
import { LoadingSpinner, PageLoader } from '../components/LoadingSpinner';
import SlabRating from '../components/SlabRating';
import { toast } from 'sonner';
import { ArrowLeft, Package, Truck, MapPin, Star, FileText, Copy, Check, AlertTriangle, DollarSign, Clock, Shield } from 'lucide-react';

export const OrderDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { api, user } = useAuth();
  
  const [order, setOrder] = useState(null);
  const [orderDetails, setOrderDetails] = useState(null);
  const [escrowStatus, setEscrowStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);
  const [packingSlip, setPackingSlip] = useState(null);
  const [copied, setCopied] = useState(false);
  
  // Dispute states
  const [disputeDialogOpen, setDisputeDialogOpen] = useState(false);
  const [disputeReason, setDisputeReason] = useState('');
  const [disputeDescription, setDisputeDescription] = useState('');
  const [submittingDispute, setSubmittingDispute] = useState(false);

  useEffect(() => {
    fetchOrder();
    fetchOrderDetails();
    fetchEscrowStatus();
    
    // Check for payment success
    const sessionId = searchParams.get('session_id');
    const paymentSuccess = searchParams.get('payment');
    if (sessionId && paymentSuccess === 'success') {
      handlePaymentSuccess(sessionId);
    }
  }, [id]);

  const fetchOrder = async () => {
    try {
      const response = await api().get(`/orders/${id}`);
      setOrder(response.data);
    } catch (error) {
      console.error('Failed to fetch order:', error);
      toast.error('Order not found');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchOrderDetails = async () => {
    try {
      const response = await api().get(`/orders/${id}/details`);
      setOrderDetails(response.data);
    } catch (error) {
      console.error('Failed to fetch order details:', error);
    }
  };

  const fetchEscrowStatus = async () => {
    try {
      const response = await api().get(`/orders/${id}/escrow-status`);
      setEscrowStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch escrow status:', error);
    }
  };

  const handleReportIssue = async () => {
    if (!disputeReason) {
      toast.error('Please select a reason');
      return;
    }
    
    setSubmittingDispute(true);
    try {
      const response = await api().post(`/orders/${id}/report-issue`, null, {
        params: { reason: disputeReason, description: disputeDescription }
      });
      
      if (response.data.success) {
        toast.success('Issue reported. Payout frozen pending review.');
        setDisputeDialogOpen(false);
        fetchEscrowStatus();
        fetchOrder();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to report issue');
    } finally {
      setSubmittingDispute(false);
    }
  };

  const handlePaymentSuccess = async (sessionId) => {
    let attempts = 0;
    const maxAttempts = 5;
    
    const pollStatus = async () => {
      try {
        const response = await api().get(`/orders/payment-status/${sessionId}`);
        if (response.data.payment_status === 'paid') {
          toast.success('Payment successful! Your order is confirmed.');
          fetchOrder();
          navigate(`/orders/${id}`, { replace: true });
          return;
        }
      } catch (error) {
        console.error('Status check error:', error);
      }
      
      attempts++;
      if (attempts < maxAttempts) {
        setTimeout(pollStatus, 2000);
      }
    };
    
    pollStatus();
  };

  const handleSubmitReview = async () => {
    if (rating === 0) {
      toast.error('Please select a rating');
      return;
    }

    setSubmittingReview(true);
    try {
      await api().post('/reviews', {
        order_id: order.id,
        rating,
        comment: comment || null
      });
      toast.success('Review submitted!');
      setReviewOpen(false);
      fetchOrder();
    } catch (error) {
      console.error('Review error:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    } finally {
      setSubmittingReview(false);
    }
  };

  const fetchPackingSlip = async () => {
    try {
      const response = await api().get(`/orders/${id}/packing-slip`);
      setPackingSlip(response.data);
    } catch (error) {
      console.error('Packing slip error:', error);
      toast.error('Failed to load packing slip');
    }
  };

  const copyTracking = () => {
    if (order?.tracking_number) {
      navigator.clipboard.writeText(order.tracking_number);
      setCopied(true);
      toast.success('Tracking number copied!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (loading) return <PageLoader />;
  if (!order) return null;

  const isBuyer = user?.id === order.buyer_id;
  const isSeller = user?.id === order.seller_id;

  return (
    <div className="min-h-screen bg-[#09090B]">
      <div className="noise-overlay" />
      <Navbar />
      
      <main className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-zinc-400 hover:text-white mb-8 transition-colors"
          data-testid="back-button"
        >
          <ArrowLeft className="w-5 h-5" />
          Back
        </button>

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading font-bold text-3xl uppercase text-white">
              Order #{order.id.slice(0, 8)}
            </h1>
            <span className={`badge mt-2 ${order.status === 'paid' ? 'badge-emerald' : 'badge-amber'}`}>
              {order.status}
            </span>
          </div>
          
          {isSeller && order.status === 'paid' && (
            <Button
              onClick={fetchPackingSlip}
              className="btn-secondary"
              data-testid="packing-slip-btn"
            >
              <FileText className="w-5 h-5 mr-2" />
              View Packing Slip
            </Button>
          )}
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Card Details */}
          <div className="stat-card p-6">
            <div className="flex gap-4">
              <div className="w-24 h-32 bg-zinc-800 rounded-lg overflow-hidden flex-shrink-0">
                {order.card_details?.image_url ? (
                  <img src={order.card_details.image_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-zinc-600 font-heading font-bold text-2xl">
                    {order.card_details?.player_name?.charAt(0)}
                  </div>
                )}
              </div>
              <div>
                <h3 className="font-heading font-bold text-xl text-white">
                  {order.card_details?.player_name}
                </h3>
                <p className="text-zinc-400 text-sm">
                  {order.card_details?.year} {order.card_details?.set_name}
                </p>
                <p className="text-zinc-500 text-sm mt-2">
                  {order.card_details?.grade_company} {order.card_details?.grade_value}
                </p>
              </div>
            </div>
          </div>

          {/* Order Summary */}
          <div className="stat-card p-6 space-y-3">
            <h3 className="font-medium text-white mb-4">Order Summary</h3>
            <div className="flex justify-between text-sm">
              <span className="text-zinc-400">Item Price</span>
              <span className="text-white">${order.item_price?.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-zinc-400">Transaction Fee (3.5%)</span>
              <span className="text-white">${order.surcharge?.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-zinc-400">Tax</span>
              <span className="text-white">${order.tax?.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-zinc-400">Shipping</span>
              <span className="text-white">${order.shipping_cost?.toFixed(2)}</span>
            </div>
            <div className="border-t border-white/10 pt-3 flex justify-between">
              <span className="font-medium text-white">Total</span>
              <span className="font-mono font-bold text-emerald-400">${order.total?.toFixed(2)}</span>
            </div>
          </div>

          {/* Seller Payout Breakdown (Seller Only) */}
          {isSeller && orderDetails?.seller_breakdown && (
            <div className="stat-card p-6 space-y-3 md:col-span-2" data-testid="seller-payout-breakdown">
              <div className="flex items-center gap-3 mb-4">
                <DollarSign className="w-6 h-6 text-emerald-400" />
                <h3 className="font-medium text-white">Your Payout Breakdown</h3>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                {/* Left: Cost Breakdown */}
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-zinc-400">Item Sale Price</span>
                    <span className="text-white">${orderDetails.seller_breakdown.items_total?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-zinc-400">Charged to Buyer (Shipping)</span>
                    <span className="text-white">${orderDetails.seller_breakdown.charged_to_buyer?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-zinc-400">Actual Shipping Cost</span>
                    <span className="text-white">${orderDetails.seller_breakdown.actual_shipping_cost?.toFixed(2)}</span>
                  </div>
                  
                  {orderDetails.seller_breakdown.shipping_subsidy > 0 && (
                    <div className="flex justify-between text-sm items-center">
                      <span className="text-amber-400 flex items-center gap-1">
                        <AlertTriangle className="w-4 h-4" />
                        Shipping Subsidy
                      </span>
                      <span className="text-amber-400">-${orderDetails.seller_breakdown.shipping_subsidy?.toFixed(2)}</span>
                    </div>
                  )}
                </div>

                {/* Right: Net Payout */}
                <div className="bg-white/5 rounded-lg p-4">
                  <div className="text-center">
                    <p className="text-zinc-400 text-sm mb-2">Your Net Payout</p>
                    <p className="text-3xl font-mono font-bold text-emerald-400">
                      ${orderDetails.seller_breakdown.net_payout?.toFixed(2)}
                    </p>
                    <p className={`text-xs mt-2 ${
                      orderDetails.seller_breakdown.payout_status === 'completed' 
                        ? 'text-emerald-400' 
                        : 'text-amber-400'
                    }`}>
                      {orderDetails.seller_breakdown.payout_status === 'completed' 
                        ? `Paid on ${new Date(orderDetails.seller_breakdown.payout_date).toLocaleDateString()}` 
                        : orderDetails.seller_breakdown.payout_status === 'pending'
                        ? 'Pending payout'
                        : 'Awaiting delivery confirmation'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Shipping Note */}
              {orderDetails.seller_breakdown.shipping_subsidy > 0 && (
                <div className="mt-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                  <p className="text-amber-200 text-sm">
                    {orderDetails.seller_breakdown.shipping_note}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Tracking */}
          {order.tracking_number && (
            <div className="stat-card p-6">
              <div className="flex items-center gap-3 mb-4">
                <Truck className="w-6 h-6 text-blue-400" />
                <h3 className="font-medium text-white">Tracking</h3>
              </div>
              <div className="flex items-center gap-2">
                <code className="flex-1 bg-white/5 px-4 py-2 rounded-lg text-zinc-300 font-mono">
                  {order.tracking_number}
                </code>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={copyTracking}
                  data-testid="copy-tracking"
                >
                  {copied ? <Check className="w-5 h-5 text-emerald-400" /> : <Copy className="w-5 h-5" />}
                </Button>
              </div>
            </div>
          )}

          {/* Escrow Status (Buyer View) */}
          {isBuyer && escrowStatus && order.status === 'delivered' && (
            <div className="stat-card p-6" data-testid="escrow-status">
              <div className="flex items-center gap-3 mb-4">
                <Shield className="w-6 h-6 text-blue-400" />
                <h3 className="font-medium text-white">Buyer Protection</h3>
              </div>
              
              {escrowStatus.escrow_status === 'pending_release' && escrowStatus.time_remaining && (
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mb-4">
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-blue-400" />
                    <div>
                      <p className="text-blue-300 font-medium">
                        {escrowStatus.time_remaining.hours}h {escrowStatus.time_remaining.minutes}m remaining
                      </p>
                      <p className="text-zinc-400 text-sm">
                        Funds held in escrow. Report any issues before the window closes.
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {escrowStatus.dispute_filed && (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 mb-4">
                  <div className="flex items-center gap-2 text-amber-300">
                    <AlertTriangle className="w-5 h-5" />
                    <span className="font-medium">Issue Reported - Under Review</span>
                  </div>
                  <p className="text-zinc-400 text-sm mt-2">
                    Payout is frozen. An admin will review your case.
                  </p>
                </div>
              )}
              
              {!escrowStatus.dispute_filed && escrowStatus.escrow_status === 'pending_release' && (
                <Button
                  variant="outline"
                  className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
                  onClick={() => setDisputeDialogOpen(true)}
                  data-testid="report-issue-btn"
                >
                  <AlertTriangle className="w-4 h-4 mr-2" />
                  Report Issue
                </Button>
              )}
              
              {escrowStatus.escrow_status === 'released' && (
                <p className="text-emerald-400 text-sm">
                  Transaction complete. Funds released to seller.
                </p>
              )}
            </div>
          )}

          {/* Shipping Address */}
          {order.shipping_address && (
            <div className="stat-card p-6">
              <div className="flex items-center gap-3 mb-4">
                <MapPin className="w-6 h-6 text-blue-400" />
                <h3 className="font-medium text-white">Shipping Address</h3>
              </div>
              <div className="text-zinc-400 space-y-1">
                <p>{order.shipping_address.name}</p>
                <p>{order.shipping_address.street}</p>
                <p>{order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.zip}</p>
                <p>{order.shipping_address.country}</p>
              </div>
            </div>
          )}
        </div>

        {/* Review Button (for buyer) */}
        {isBuyer && order.status === 'paid' && (
          <div className="mt-8">
            <Button
              onClick={() => setReviewOpen(true)}
              className="btn-secondary"
              data-testid="leave-review-btn"
            >
              <Star className="w-5 h-5 mr-2" />
              Leave a Review
            </Button>
          </div>
        )}
      </main>

      {/* Review Dialog */}
      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent className="bg-[#18181B] border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white font-heading text-2xl">Leave a Review</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 mt-4">
            <div className="text-center">
              <p className="text-zinc-400 mb-4">How was your experience?</p>
              <div className="flex justify-center">
                <SlabRating rating={rating} onRate={setRating} size="lg" />
              </div>
            </div>
            
            <div>
              <Textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Write your review (optional)..."
                className="input-field min-h-[120px] resize-none"
                data-testid="review-comment"
              />
            </div>
            
            <Button
              onClick={handleSubmitReview}
              disabled={submittingReview}
              className="w-full btn-primary"
              data-testid="submit-review"
            >
              {submittingReview ? <LoadingSpinner size="sm" /> : 'Submit Review'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Packing Slip Dialog */}
      <Dialog open={!!packingSlip} onOpenChange={() => setPackingSlip(null)}>
        <DialogContent className="bg-[#18181B] border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white font-heading text-2xl">Packing Slip</DialogTitle>
          </DialogHeader>
          
          {packingSlip && (
            <div className="space-y-6 mt-4 text-sm">
              <div className="stat-card p-4">
                <p className="text-zinc-400">Order ID</p>
                <p className="text-white font-mono">{packingSlip.order_id}</p>
              </div>
              
              <div className="stat-card p-4">
                <p className="text-zinc-400 mb-2">Card</p>
                <p className="text-white font-medium">{packingSlip.card?.player_name}</p>
                <p className="text-zinc-400">{packingSlip.card?.year} {packingSlip.card?.set_name}</p>
                <p className="text-zinc-500">{packingSlip.card?.grade_company} {packingSlip.card?.grade_value}</p>
              </div>
              
              <div className="stat-card p-4">
                <p className="text-zinc-400 mb-2">Ship To</p>
                <p className="text-white">{packingSlip.buyer_name}</p>
                {packingSlip.shipping_address && (
                  <div className="text-zinc-400 mt-1">
                    <p>{packingSlip.shipping_address.street}</p>
                    <p>{packingSlip.shipping_address.city}, {packingSlip.shipping_address.state} {packingSlip.shipping_address.zip}</p>
                  </div>
                )}
              </div>
              
              {packingSlip.tracking_number && (
                <div className="stat-card p-4">
                  <p className="text-zinc-400">Tracking Number</p>
                  <p className="text-white font-mono">{packingSlip.tracking_number}</p>
                </div>
              )}
              
              <Button
                onClick={() => window.print()}
                className="w-full btn-secondary"
              >
                Print Packing Slip
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Report Issue Dialog */}
      <Dialog open={disputeDialogOpen} onOpenChange={setDisputeDialogOpen}>
        <DialogContent className="bg-[#18181B] border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white font-heading text-2xl">Report an Issue</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Filing an issue will freeze the seller's payout until an admin reviews your case.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm text-zinc-400 mb-2 block">Reason</label>
              <Select value={disputeReason} onValueChange={setDisputeReason}>
                <SelectTrigger className="input-field" data-testid="dispute-reason-select">
                  <SelectValue placeholder="Select a reason" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Item not as described">Item not as described</SelectItem>
                  <SelectItem value="Item damaged">Item damaged during shipping</SelectItem>
                  <SelectItem value="Wrong item received">Wrong item received</SelectItem>
                  <SelectItem value="Item never arrived">Item never arrived</SelectItem>
                  <SelectItem value="Counterfeit or fake">Counterfeit or fake item</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-sm text-zinc-400 mb-2 block">Description (optional)</label>
              <Textarea
                value={disputeDescription}
                onChange={(e) => setDisputeDescription(e.target.value)}
                placeholder="Please provide details about the issue..."
                className="input-field min-h-[100px] resize-none"
                data-testid="dispute-description"
              />
            </div>
            
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
              <p className="text-amber-300 text-sm">
                <AlertTriangle className="w-4 h-4 inline mr-2" />
                This will freeze the seller's payout until resolved.
              </p>
            </div>
            
            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                className="flex-1 border-zinc-700"
                onClick={() => setDisputeDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleReportIssue}
                disabled={submittingDispute || !disputeReason}
                className="flex-1 bg-amber-600 hover:bg-amber-700"
                data-testid="submit-dispute-btn"
              >
                {submittingDispute ? <LoadingSpinner size="sm" /> : 'Submit Report'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrderDetailPage;
